import { PropertyTag } from '@go-shared/types/property';
import { ObjectID } from 'bson';
import React, { useEffect, useState } from 'react';
import uuid from 'uuid';
import {
  BubbleInput,
  BubbleTypeahead,
  BubbleButton,
  BubbleFormGroup,
  BubbleSelect,
} from './BubbleUI';
import { BubbleTypeaheadValue, BubbleTypeaheadOption } from './BubbleUI/BubbleTypeahead';
import { Alert } from '@spark-labs/avocado-ui';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { mapBy } from '@go-shared/lib/functional';

interface TemplateFormProps {
  submitButtonTitle?: string;
  initialForm?: TemplateFormDataBase;
  propertyTags?: PropertyTag[];
  onUpdatePropertyTags?: (tags: PropertyTag[]) => Promise<unknown>;
  onSubmit?: (templateData: TemplateFormDataBase) => Promise<unknown>;
}

export interface TemplateFormDataBase {
  name: string;
  dayTheme?: string;
  placeholder?: string;
  tags: PropertyTag[];
}

type TemplateFormData = TemplateFormDataBase & {
  _dirty: boolean;
  _errors: Partial<TemplateFormError>;
};

type TemplateFormError = { [K in keyof TemplateFormDataBase]: string };

function DEFAULT_FORM_DATA(initialForm?: TemplateFormDataBase): TemplateFormData {
  return {
    name: initialForm?.name ?? '',
    placeholder: initialForm?.placeholder ?? undefined,
    dayTheme: initialForm?.dayTheme ?? undefined,
    tags: initialForm?.tags ?? [],
    _dirty: false,
    _errors: {},
  };
}

export default function TemplateForm(props: TemplateFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA(props.initialForm));

  const { brand } = useServerInfo();
  const dayThemeOpts = mapBy(brand.dayThemes, '_id');

  const tagOptions = (props.propertyTags || []).map(t => ({ key: t._id, item: t }));
  const selectedTags = (formData ? formData.tags : []).map(t => ({
    key: uuid(),
    refKey: t._id,
    item: t,
  }));

  const errors = formData._errors;
  const errored = !!Object.values(errors).find(v => v !== undefined);

  useEffect(() => {
    if (!formData._dirty) resetForm();
  }, [props.initialForm]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.initialForm));
  }

  function changeForm<T extends keyof TemplateFormDataBase>(key: T, val: TemplateFormDataBase[T]) {
    const newErrors = { ...formData._errors };
    delete newErrors[key];
    setFormData({ ...formData, [key]: val, _dirty: true, _errors: newErrors });
  }

  async function submitForm() {
    if (!props.onSubmit) return;

    if (checkErrors()) return;

    setSaving(true);

    try {
      await props.onSubmit({
        name: formData.name,
        dayTheme: formData.dayTheme,
        tags: selectedTags.map(st => st.item),
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleOnCreateTag(name: string) {
    if (!props.onUpdatePropertyTags) return;

    setSaving(true);

    try {
      const newTag = {
        _id: new ObjectID().toHexString(),
        name,
      };

      await props.onUpdatePropertyTags([...(props.propertyTags || []), newTag]);

      changeForm('tags', [...formData.tags, newTag]);
    } finally {
      setSaving(false);
    }
  }

  async function handleOnAddTag(tag: BubbleTypeaheadValue<PropertyTag>) {
    changeForm('tags', [...formData.tags, tag.item]);
  }

  async function handleOnDeleteTag(tag: BubbleTypeaheadValue<PropertyTag>) {
    changeForm(
      'tags',
      formData.tags.filter(st => st._id !== tag.item._id)
    );
  }

  async function handleOnRemoveTag(opt: BubbleTypeaheadOption<PropertyTag>) {
    if (!props.onUpdatePropertyTags) return;

    let shouldContinue = await Alert.question(
      `Are you sure you want to remove the tag` +
        ` [${opt.item.name}] from all its associated templates?`
    );

    if (!shouldContinue) return;

    setSaving(true);

    try {
      await props.onUpdatePropertyTags(
        (props.propertyTags || []).filter(t => t._id !== opt.item._id)
      );

      changeForm(
        'tags',
        formData.tags.filter(t => t._id !== opt.item._id)
      );
    } finally {
      setSaving(false);
    }
  }

  function checkErrors() {
    let hasErrors = false;

    const newErrors: TemplateFormError = {
      name: '',
      tags: '',
    };

    if (formData.name.length < 3) {
      newErrors.name = 'A template name must consist of at least three characters.';
      hasErrors = true;
    }

    setFormData({ ...formData, _errors: newErrors });

    return hasErrors;
  }

  return (
    <div className="grid grid-cols-1 gap-5">
      <BubbleFormGroup label="Name">
        <BubbleInput
          block
          type="text"
          value={formData.name}
          placeholder={formData.placeholder}
          onValue={val => changeForm('name', val)}
        />
        {errors.name && <div className="text-crimson mt-1">{errors.name}</div>}
      </BubbleFormGroup>
      <BubbleFormGroup label="Tags">
        <BubbleTypeahead
          labelExtractor={itm => itm.name}
          value={selectedTags}
          menuClassName="max-h-80 overflow-y-auto"
          options={tagOptions}
          placeholder="Search... (or create a new one)"
          allowNew
          onCreate={handleOnCreateTag}
          onAdd={handleOnAddTag}
          onRemove={handleOnDeleteTag}
          onTrash={handleOnRemoveTag}
        />
        {errors.tags && <div className="text-crimson mt-1">{errors.tags}</div>}
      </BubbleFormGroup>
      <BubbleFormGroup label="Day Theme">
        <BubbleSelect
          block
          allowUndefined
          labelExtractor={id => dayThemeOpts[id].name}
          value={formData.dayTheme}
          options={brand.dayThemes.map(s => s._id)}
          onValue={val => changeForm('dayTheme', val)}
        />
      </BubbleFormGroup>
      <div>
        <BubbleButton
          variant="navy"
          onClick={submitForm}
          disabled={!formData._dirty || errored || saving || !formData.name}
          icon="floppy-disk"
        >
          {props.submitButtonTitle || 'Save Changes'}
        </BubbleButton>
        <BubbleButton
          variant="navy"
          onClick={resetForm}
          inverse
          disabled={saving}
          icon="rotate-left"
        >
          Reset
        </BubbleButton>
      </div>
    </div>
  );
}
